import { useEffect, useRef, useState } from 'react';

import { Box } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { Location } from 'history';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useHistory } from 'react-router';

import CampaignStatusText from 'domains/campaigns/pages/CurationsPage/CampaignsTable/CampaignStatusText';
import {
    ABTestType,
    AudienceType,
    CampaignChannels,
    DirectMailSendInput,
    DirectMailSendInputState,
    EmailSendInputState,
    SmsSendInput,
    SmsSendInputState,
    SplitTypes,
    VoiceCallSendInput,
} from 'domains/campaigns/types';
import { getExitSegmentDefinition, squashBlankTimedelaySends } from 'domains/campaigns/utils';
import ContentUnavailableAlert from 'domains/core/components/ContentUnavailableAlert';
import Flow from 'domains/core/components/Flow';
import RouteLeavingGuard from 'domains/core/components/RouteLeavingGuard';
import {
    campaignDialogProps,
    draftCampaignDialogProps,
} from 'domains/core/components/RouteLeavingGuard/RouteLeavingGuard';
import ErrorSnackbar from 'domains/core/components/Snackbars/ErrorSnackbar';
import SuccessDialog from 'domains/core/components/SuccessDialog';
import { trackCuredEvent, MixpanelEvent, MixpanelFlow, CampaignFlowType } from 'helpers/initMixpanel';
import { recordSatismeterEvent } from 'helpers/injectSatismeter/injectSatismeter';
import useCreateCampaign from 'hooks/mutations/useCreateCampaign';
import useCreateDraftCampaign from 'hooks/mutations/useCreateDraftCampaign';
import useUpdateAndResumeCampaign from 'hooks/mutations/useUpdateAndResumeCampaign';
import useUpdateDraftCampaign from 'hooks/mutations/useUpdateDraftCampaign';
import QueryKeys from 'hooks/queries/keys';
import useQueryParams from 'hooks/useQueryParams';
import { CampaignStatus, URLPaths } from 'models/enums';

import { removeSend, setName } from './customCampaignActions';
import CustomCampaignContentPreview from './CustomCampaignContentPreview';
import CustomCampaignForm from './CustomCampaignForm';
import CustomCampaignNameInput from './CustomCampaignNameInput';
import CustomCampaignNavigation from './CustomCampaignNavigation';
import CustomCampaignNavigationButtons from './CustomCampaignNavigationButtons';
import {
    CampaignStateTypes,
    emailInitialState,
    directMailInitialState,
    smsInitialState,
    voiceCallInitialState,
} from './customCampaignReducer';
import GraphCampaignNavigation from './GraphCampaignNavigation';
import SplitRewireConfirmDialog, { AffectedReference } from './SplitRewireConfirmDialog';
import { getEffectiveSplitType, getEmailSendForApi, getSegmentsForApi, isCampaignSendsComplete } from './utils';

const DraftSuccessBody =
    "Nice work, you've successfully save your Curation as draft. Continue editing the curation or exit and return to it later.";
const PublishSuccessBody =
    "Nice work, you've successfully scheduled your Curation. Once it sends, visit Insights to monitor its performance.";

export enum CustomCampaignSteps {
    NAME_CAMPAIGN = 'name-campaign',
    SELECT_AUDIENCE = 'select-audience',
    SELECT_CONTENT = 'select-content',
    CHOOSE_NEXT_STEP = 'choose-next-step',
    CHOOSE_NEXT_STEP_CONDITIONAL_SPLIT = 'choose-next-step-conditional-split',
    SET_TIME_DELAY = 'set-time-delay',
    CHOOSE_SEND_TYPE = 'choose-send-type',
    CONDITIONAL_SPLIT = 'conditional-split',
    RANDOM_SPLIT = 'random-split',
    CHOOSE_NEXT_STEP_RANDOM_SPLIT = 'choose-next-step-random-split',
    SELECT_PATH_ACTION = 'select-path-action',
    SCHEDULE_CURATION = 'schedule-curation',
    PUBLISH_CAMPAIGN = 'publish-campaign',
    SETUP_A_B_TEST = 'setup-a-b-test',
}

const CustomCampaign = ({
    campaignState,
    dispatchCampaignAction,
    campaignId,
}: {
    campaignState: CampaignStateTypes;
    dispatchCampaignAction: React.Dispatch<any>;
    campaignId?: string;
}) => {
    // Navigation State Values
    const [activeStep, setActiveStep] = useState(CustomCampaignSteps.NAME_CAMPAIGN);
    const [nextStep, setNextStep] = useState(CustomCampaignSteps.SELECT_AUDIENCE);
    const [activeSendIndex, setActiveSendIndex] = useState(0);

    const [contentPreviewId, setContentPreviewId] = useState(null);
    const [customContinueHandler, setCustomContinueHandler] = useState<(() => void) | null>(null);
    const [initialContentSubStep, setInitialContentSubStep] = useState<'selecting' | 'scheduling' | undefined>(
        undefined
    );

    const {
        multipleSendsConditionalSplit: hasMultipleSendsConditionalSplitFlag,
        campaignGraphNavigation: hasCampaignGraphNavigationFlag,
    } = useFlags();
    // Custom continue handler setter with React functional update fix
    const setCustomContinueHandlerFixed = (handler: (() => void) | null) => {
        if (handler) {
            // FIX: Use functional update pattern to prevent React from calling the handler immediately
            // React treats functions passed to setState as potential functional updates
            // By wrapping in () => handler, React calls the wrapper and gets the actual handler
            setCustomContinueHandler(() => handler);
        } else {
            setCustomContinueHandler(null);
        }
    };

    const [isContinueEnabled, setIsContinueEnabled] = useState(false);
    const [continueTooltip, setContinueTooltip] = useState<string>('');
    const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
    const [isSaveDraftClicked, setIsSaveDraftClicked] = useState(false);
    const [savedDraftCampaignId, setSavedDraftCampaignId] = useState<string | null>(null);
    // Track if content is unavailable (unpublished/deleted) for showing page-level alert
    const [isContentUnavailable, setIsContentUnavailable] = useState(false);
    const [unavailableContentId, setUnavailableContentId] = useState<number | null>(null);

    const query = useQueryParams();
    const statusParam = query.get('status');
    const status = statusParam ? (statusParam as CampaignStatus) : null;
    const disableNameEditing = status === CampaignStatus.PAUSED;
    const isEditingPausedCuration = status === CampaignStatus.PAUSED;

    // A branch send opened via ADD_SPLIT_NODE/addBranchSend defaults to splitAction SEND before
    // the user has picked content — so it must have real content selected, not just an action
    // set, before this curation can be published. Otherwise a branch can be left unconfigured
    // and still get published, which is what made the details page look incomplete afterward.
    const isPublishEnabled = isCampaignSendsComplete(campaignState.sends);
    const publishDisabledTooltip = isPublishEnabled
        ? ''
        : 'Every path in this curation needs content selected before it can be published.';

    // Snapshot the initial sends once the campaign data has been loaded from the API
    // (indicated by any send having a persisted DB id). Used to detect content changes
    // that would invalidate downstream conditional-split activity filters.
    const initialSendsRef = useRef<any[]>([]);
    const hasSnapshottedRef = useRef(false);
    useEffect(() => {
        if (isEditingPausedCuration && !hasSnapshottedRef.current && campaignState.sends.some((s) => (s as any).id)) {
            initialSendsRef.current = [...campaignState.sends];
            hasSnapshottedRef.current = true;
        }
    }, [isEditingPausedCuration, campaignState.sends]);

    const [splitRewireDialogOpen, setSplitRewireDialogOpen] = useState(false);
    const [splitRewireAffected, setSplitRewireAffected] = useState<AffectedReference[]>([]);
    // 'save' = update without resuming; 'saveAndResume' = update + resume
    const pendingPausedActionRef = useRef<'save' | 'saveAndResume' | null>(null);

    // Strip non-content fields from NOOP sends when editing paused curations
    // Only keep minimal identifying fields to avoid API validation issues
    const sanitizeSendForPausedEdit = (send: any) => {
        if (send.channel === CampaignChannels.NOOP) {
            return {
                channel: CampaignChannels.NOOP,
                parentIndex: send.parentIndex,
                splitAction: send.splitAction,
                splitType: send.splitType,
            };
        }
        return send;
    };

    // Handle content unavailable state change
    const handleContentUnavailableChange = (isUnavailable: boolean, contentId?: number | null) => {
        setIsContentUnavailable(isUnavailable);
        setUnavailableContentId(contentId ?? null);
    };

    // Helper function to navigate to A/B test scheduling for specific email
    const navigateToABTestScheduling = (emailIndex: number) => {
        setActiveSendIndex(emailIndex);
        setInitialContentSubStep('scheduling');
        setActiveStep(CustomCampaignSteps.SELECT_CONTENT);
    };

    // Reset initialContentSubStep when leaving the content step
    useEffect(() => {
        if (activeStep !== CustomCampaignSteps.SELECT_CONTENT) {
            setInitialContentSubStep(undefined);
        }
    }, [activeStep]);

    const history = useHistory();
    const queryClient = useQueryClient();

    const {
        mutate,
        isError: isCreateCampaignError,
        isLoading: isCreateCampaignLoading,
        error: createCampaignError,
    } = useCreateCampaign(campaignId, {
        onSuccess: (data) => {
            // Invalidate queries used for the sends and campaigns tables
            queryClient.invalidateQueries(QueryKeys.CAMPAIGNS_BASE);
            setIsSuccessDialogOpen(true);

            // Track campaign published
            const firstSend = campaignState.sends?.[0];
            trackCuredEvent(
                MixpanelEvent.CAMPAIGN_PUBLISHED,
                {
                    campaign_id: data?.id || campaignId,
                    campaign_type: firstSend?.channel || 'unknown',
                    flow_type: CampaignFlowType.CUSTOM_CAMPAIGN,
                    campaign_name: campaignState.name,
                },
                MixpanelFlow.CAMPAIGN
            );
        },
    });

    // Separate mutation for publish and resume (calls PUT /campaigns/:id?resume=true)
    const {
        mutate: mutatePublishAndResume,
        isError: isPublishAndResumeError,
        isLoading: isPublishAndResumeLoading,
        error: publishAndResumeError,
    } = useUpdateAndResumeCampaign(campaignId || '', {
        onSuccess: () => {
            // Invalidate queries used for the sends and campaigns tables
            queryClient.invalidateQueries(QueryKeys.CAMPAIGNS_BASE);
            setIsSuccessDialogOpen(true);
        },
    });

    const {
        mutate: mutateCreateDraft,
        isError: isCreateDraftCampaignError,
        isLoading: isCreateDraftCampaignLoading,
        error: createDraftCampaignError,
    } = useCreateDraftCampaign({
        onSuccess: (data) => {
            // Invalidate queries used for the sends and campaigns tables
            queryClient.invalidateQueries(QueryKeys.CAMPAIGNS_BASE);
            // Store the campaign ID for navigation
            setSavedDraftCampaignId(data.id);
            setIsSuccessDialogOpen(true);

            // Track draft campaign created
            const firstSend = campaignState.sends?.[0];
            trackCuredEvent(
                MixpanelEvent.DRAFT_CAMPAIGN_SAVED,
                {
                    campaign_id: data?.id,
                    campaign_type: firstSend?.channel || 'unknown',
                    flow_type: CampaignFlowType.CUSTOM_CAMPAIGN,
                    campaign_name: campaignState.name,
                    is_new_draft: true,
                },
                MixpanelFlow.CAMPAIGN
            );
        },
    });

    const {
        mutate: mutateUpdateDraft,
        isError: isUpdateDraftCampaignError,
        isLoading: isUpdateDraftCampaignLoading,
        error: updateDraftCampaignError,
    } = useUpdateDraftCampaign(campaignId || '', {
        onSuccess: () => {
            // Invalidate queries used for the sends and campaigns tables
            queryClient.invalidateQueries(QueryKeys.CAMPAIGNS_BASE);
            // For update, use the existing campaignId
            setSavedDraftCampaignId(campaignId || null);
            setIsSuccessDialogOpen(true);

            // Track draft campaign updated
            const firstSend = campaignState.sends?.[0];
            trackCuredEvent(
                MixpanelEvent.DRAFT_CAMPAIGN_SAVED,
                {
                    campaign_id: campaignId,
                    campaign_type: firstSend?.channel || 'unknown',
                    flow_type: CampaignFlowType.CUSTOM_CAMPAIGN,
                    campaign_name: campaignState.name,
                    is_new_draft: false,
                },
                MixpanelFlow.CAMPAIGN
            );
        },
    });

    // Closing content preview when switching steps
    useEffect(() => {
        setContentPreviewId(null);
    }, [activeStep]);

    const getDirectMailSendForApi = (send: DirectMailSendInputState, index: number): DirectMailSendInput => {
        const { content, segments: rawSegments, ...restOfSend } = send as any;

        const sendForApi: any = {
            ...restOfSend,
            contentId: content.id,
            name: send.name,
            segmentId: send.segmentId || null,
        };

        if (rawSegments?.length > 0) {
            getSegmentsForApi({ ...send, segments: rawSegments } as any, sendForApi, campaignState.sends);
        }

        return sendForApi;
    };

    const getSMSSendForApi = (send: SmsSendInputState, index: number): SmsSendInput => {
        const { content, segments: rawSegments, ...restOfSend } = send as any;

        const sendForApi: any = {
            ...restOfSend,
            smsContentId: content.id,
            name: send.name,
            segmentId: (send as any).segmentId ?? null,
            cohortId: (send as any).cohortId ?? null,
            smsSenderProfileId: (send as any).smsSenderProfileId ?? null,
        };

        if (rawSegments?.length > 0) {
            getSegmentsForApi({ ...send, segments: rawSegments } as any, sendForApi, campaignState.sends);
        }

        return sendForApi;
    };

    const getVoiceSendForApi = (send: VoiceCallSendInput, index: number): VoiceCallSendInput => {
        const { segments: rawSegments, ...restOfSend } = send as any;

        const sendForApi: any = {
            ...restOfSend,
            name: send.name,
            segmentId: (send as any).segmentId || null,
        };

        if (rawSegments?.length > 0) {
            getSegmentsForApi({ ...send, segments: rawSegments } as any, sendForApi, campaignState.sends);
        }

        return sendForApi;
    };

    // Helper function to get the appropriate initial state based on the primary channel
    const getInitialStateForChannel = (campaignState: CampaignStateTypes): CampaignStateTypes => {
        // Determine the primary channel from the first send or default to EMAIL
        const primaryChannel = campaignState.sends?.[0]?.channel || CampaignChannels.EMAIL;

        switch (primaryChannel) {
            case CampaignChannels.DIRECT_MAIL:
                return directMailInitialState;
            case CampaignChannels.SMS:
                return smsInitialState;
            case CampaignChannels.VOICE_CALL:
                return voiceCallInitialState;
            case CampaignChannels.EMAIL:
            default:
                return emailInitialState;
        }
    };

    const onContinueClick = () => {
        // Track events when user confirms their selection by clicking Continue
        const activeSend = campaignState.sends[activeSendIndex];
        // Track Audience Selected
        if (activeStep === CustomCampaignSteps.SELECT_AUDIENCE) {
            const firstSend = campaignState.sends[0];
            if (firstSend.cohortId) {
                trackCuredEvent(
                    MixpanelEvent.AUDIENCE_SELECTED,
                    {
                        campaign_type: firstSend.channel,
                        flow_type: CampaignFlowType.CUSTOM_CAMPAIGN,
                        audience_type: AudienceType.COHORT,
                        audience_id: firstSend.cohortId,
                    },
                    MixpanelFlow.CAMPAIGN
                );
            } else if (firstSend.segmentId) {
                trackCuredEvent(
                    MixpanelEvent.AUDIENCE_SELECTED,
                    {
                        campaign_type: firstSend.channel,
                        flow_type: CampaignFlowType.CUSTOM_CAMPAIGN,
                        audience_type: AudienceType.AUDIENCE,
                        audience_id: firstSend.segmentId,
                    },
                    MixpanelFlow.CAMPAIGN
                );
            }
        }

        // Track Content Added
        if (activeStep === CustomCampaignSteps.SELECT_CONTENT) {
            // Extract content ID and name based on channel type
            let contentId: number | null = null;
            let contentName: string = 'unknown';

            if (activeSend.channel === CampaignChannels.EMAIL) {
                const emailSend = activeSend as EmailSendInputState;
                contentId = emailSend.templateId;
                // Email sends don't store template name in state
            } else {
                // SMS, Direct Mail, and Voice Call all use the content object pattern
                const sendWithContent = activeSend as SmsSendInputState | DirectMailSendInputState | VoiceCallSendInput;
                if (sendWithContent.content) {
                    contentId = sendWithContent.content.id;
                    contentName = sendWithContent.content.name || 'unknown';
                }
            }

            if (contentId) {
                trackCuredEvent(
                    MixpanelEvent.CONTENT_ADDED,
                    {
                        campaign_type: activeSend.channel,
                        flow_type: CampaignFlowType.CUSTOM_CAMPAIGN,
                        content_id: contentId,
                        content_name: contentName,
                    },
                    MixpanelFlow.CAMPAIGN
                );
            }
        }

        // Track Schedule Set
        if (activeStep === CustomCampaignSteps.SCHEDULE_CURATION) {
            if (activeSend.scheduledTimestamp) {
                trackCuredEvent(
                    MixpanelEvent.SCHEDULE_SET,
                    {
                        campaign_type: activeSend.channel,
                        flow_type: CampaignFlowType.CUSTOM_CAMPAIGN,
                        scheduled_timestamp: activeSend.scheduledTimestamp,
                    },
                    MixpanelFlow.CAMPAIGN
                );
            }
        }

        // Check if there's a custom continue handler first
        if (customContinueHandler) {
            customContinueHandler();
            return;
        }

        // Default continue logic
        const isActiveSendEmail = activeSend?.channel === CampaignChannels.EMAIL;

        const effectiveSplitType = getEffectiveSplitType(activeSend as any, campaignState.sends);

        if (
            activeStep === CustomCampaignSteps.SELECT_CONTENT &&
            isActiveSendEmail &&
            (effectiveSplitType === SplitTypes.RANDOM_GROUP_1 || effectiveSplitType === SplitTypes.RANDOM_GROUP_2)
        ) {
            const nextSend = campaignState.sends[activeSendIndex + 1] as any;
            const isLastInBranch = !nextSend || nextSend.parentIndex !== activeSendIndex;

            if (isLastInBranch) {
                setActiveStep(CustomCampaignSteps.CHOOSE_NEXT_STEP_RANDOM_SPLIT);
            } else {
                setActiveSendIndex(activeSendIndex + 1);
                setActiveStep(CustomCampaignSteps.SET_TIME_DELAY);
            }
            return;
        }

        if (
            hasMultipleSendsConditionalSplitFlag &&
            activeStep === CustomCampaignSteps.SELECT_CONTENT &&
            !!effectiveSplitType
        ) {
            const nextSend = campaignState.sends[activeSendIndex + 1] as any;
            const isLastInBranch = !nextSend || nextSend.parentIndex !== activeSendIndex;

            if (isLastInBranch) {
                setActiveStep(CustomCampaignSteps.CHOOSE_NEXT_STEP_CONDITIONAL_SPLIT);
            } else {
                setActiveSendIndex(activeSendIndex + 1);
                setActiveStep(CustomCampaignSteps.SET_TIME_DELAY);
            }
            return;
        }

        // A/B Testing Content Flow: increment activeSendIndex when going from Email-A to Email-B
        const isABTestingContent = campaignState.abTestConfig?.testOn === ABTestType.CONTENT;
        const isEmailA = isABTestingContent && activeSendIndex === 0;

        if (
            activeStep === CustomCampaignSteps.SELECT_CONTENT &&
            ((activeSend as any)?.splitType === SplitTypes.MEETS || (isActiveSendEmail && isEmailA))
        ) {
            setActiveSendIndex(activeSendIndex + 1);
        }
        setActiveStep(nextStep);
    };

    const getSendsForApi = () =>
        campaignState.sends.map((send, index) => {
            let processedSend;
            if (send.channel === CampaignChannels.EMAIL) {
                processedSend = getEmailSendForApi(send as EmailSendInputState, index, campaignState);
            } else if (send.channel === CampaignChannels.DIRECT_MAIL) {
                processedSend = getDirectMailSendForApi(send as DirectMailSendInputState, index);
            } else if (send.channel === CampaignChannels.SMS) {
                processedSend = getSMSSendForApi(send as SmsSendInputState, index);
            } else if (send.channel === CampaignChannels.VOICE_CALL) {
                processedSend = getVoiceSendForApi(send as VoiceCallSendInput, index);
            } else {
                processedSend = send;
            }

            // Sanitize NOOP sends when editing paused curations
            if (isEditingPausedCuration) {
                return sanitizeSendForPausedEdit(processedSend);
            }
            return processedSend;
        });

    const buildCampaignStateForAPI = () => ({
        ...campaignState,
        sends: squashBlankTimedelaySends(getSendsForApi()),
        exitSegmentDefinition: getExitSegmentDefinition(campaignState),
    });

    const doPublish = () => {
        mutate(buildCampaignStateForAPI());
        recordSatismeterEvent('publish_curation');
    };

    const doPublishAndResume = () => {
        mutatePublishAndResume(buildCampaignStateForAPI());
        recordSatismeterEvent('publish_curation');
    };

    // Returns all downstream sends whose activity filter references a send whose content
    // changed since the initial load. Only meaningful when isEditingPausedCuration.
    const detectAffectedSplitReferences = (): AffectedReference[] => {
        const originalSends = initialSendsRef.current;
        if (!originalSends.length) return [];

        const changedSendIds = new Set<number>();
        for (let i = 0; i < originalSends.length; i++) {
            const original = originalSends[i] as any;
            if (!original?.id) continue;

            const current = campaignState.sends[i] as any;
            const channel = original.channel as CampaignChannels;

            let contentChanged = false;
            if (channel === CampaignChannels.EMAIL) {
                contentChanged =
                    current?.templateId !== original.templateId ||
                    current?.senderProfileId !== original.senderProfileId ||
                    current?.subjectLine !== original.subjectLine ||
                    current?.preHeader !== original.preHeader;
            } else if (channel === CampaignChannels.SMS) {
                contentChanged =
                    current?.content?.id !== original.content?.id ||
                    current?.smsSenderProfileId !== original.smsSenderProfileId;
            } else if (channel === CampaignChannels.VOICE_CALL) {
                contentChanged =
                    current?.content?.id !== original.content?.id ||
                    current?.callerProfileId !== original.callerProfileId;
            } else if (channel === CampaignChannels.DIRECT_MAIL) {
                contentChanged =
                    current?.content?.id !== original.content?.id ||
                    current?.senderProfileId !== original.senderProfileId;
            }

            if (contentChanged) changedSendIds.add(original.id);
        }

        if (!changedSendIds.size) return [];

        const affected: AffectedReference[] = [];
        for (let j = 0; j < campaignState.sends.length; j++) {
            const send = campaignState.sends[j] as any;
            for (const seg of send?.segments ?? []) {
                const refId = (seg as any).value;
                if (typeof refId === 'number' && refId > 0 && changedSendIds.has(refId)) {
                    const changedSend = originalSends.find((s: any) => s.id === refId) as any;
                    const referencingSend = originalSends[j] as any;
                    affected.push({
                        changedSendName: changedSend?.name ?? `Send ${refId}`,
                        referencingSendName: referencingSend?.name ?? `Send at index ${j}`,
                    });
                }
            }
        }
        return affected;
    };

    const onPublishClick = () => {
        if (isEditingPausedCuration) {
            const affected = detectAffectedSplitReferences();
            if (affected.length > 0) {
                setSplitRewireAffected(affected);
                pendingPausedActionRef.current = 'save';
                setSplitRewireDialogOpen(true);
                return;
            }
        }
        doPublish();
    };

    const onPublishAndResumeClick = () => {
        const affected = detectAffectedSplitReferences();
        if (affected.length > 0) {
            setSplitRewireAffected(affected);
            pendingPausedActionRef.current = 'saveAndResume';
            setSplitRewireDialogOpen(true);
            return;
        }
        doPublishAndResume();
    };

    const onSaveDraftClick = () => {
        const sendsForApi = (campaignState.sends || []).map((send, index) => {
            if (send.channel === CampaignChannels.EMAIL) {
                return getEmailSendForApi(send as EmailSendInputState, index, campaignState);
            }
            if (send.channel === CampaignChannels.DIRECT_MAIL) {
                return getDirectMailSendForApi(send as DirectMailSendInputState, index);
            }
            if (send.channel === CampaignChannels.SMS) {
                return getSMSSendForApi(send as SmsSendInputState, index);
            }
            if (send.channel === CampaignChannels.VOICE_CALL) {
                return getVoiceSendForApi(send as VoiceCallSendInput, index);
            }
            return send;
        });

        const appropriateInitialState = getInitialStateForChannel(campaignState);
        const campaignStateForAPI = {
            ...appropriateInitialState,
            ...campaignState,
            sends: sendsForApi,
        };

        setIsSaveDraftClicked(true);
        if (campaignId) {
            mutateUpdateDraft(campaignStateForAPI);
        } else {
            mutateCreateDraft(campaignStateForAPI);
        }
    };

    const onRemoveClick = (index: number) => {
        if (index === activeSendIndex) {
            setActiveSendIndex(activeSendIndex - 1);
            setActiveStep(CustomCampaignSteps.CHOOSE_NEXT_STEP);
        }

        dispatchCampaignAction(removeSend());
    };

    const handleExitCurationWorkflow = (path: string) => {
        history.push(path);
        recordSatismeterEvent('publish_curation_popup');
    };

    const getIsNavigationBlocked = (location: Location): boolean =>
        !location.pathname.includes(URLPaths.CURATIONS_CUSTOM) && !isSuccessDialogOpen;

    const navigationSx = {
        height: '100%',
        marginLeft: '5rem',
    };

    return (
        <Flow
            feedback={
                <>
                    {isContentUnavailable && (
                        <ContentUnavailableAlert
                            contentId={unavailableContentId}
                            channel={campaignState.sends[activeSendIndex]?.channel}
                        />
                    )}
                    {isCreateCampaignError && !isCreateCampaignLoading && (
                        <ErrorSnackbar errorMessage={createCampaignError.message} />
                    )}
                    {isPublishAndResumeError && !isPublishAndResumeLoading && (
                        <ErrorSnackbar errorMessage={publishAndResumeError.message} />
                    )}
                    {isCreateDraftCampaignError && !isCreateDraftCampaignLoading && (
                        <ErrorSnackbar errorMessage={createDraftCampaignError.message} />
                    )}
                    {isUpdateDraftCampaignError && !isUpdateDraftCampaignLoading && (
                        <ErrorSnackbar errorMessage={updateDraftCampaignError.message} />
                    )}
                    <RouteLeavingGuard
                        dialogProps={
                            status === CampaignStatus.DRAFT && activeStep !== CustomCampaignSteps.NAME_CAMPAIGN
                                ? draftCampaignDialogProps
                                : campaignDialogProps
                        }
                        shouldBlockNavigation={getIsNavigationBlocked}
                        onAbandon={(destination: string) => {
                            trackCuredEvent(
                                MixpanelEvent.CAMPAIGN_ABANDONED,
                                {
                                    campaign_type: campaignState.sends?.[0]?.channel || 'unknown',
                                    flow_type: CampaignFlowType.CUSTOM_CAMPAIGN,
                                    destination,
                                },
                                MixpanelFlow.CAMPAIGN
                            );
                        }}
                        {...(status === CampaignStatus.DRAFT && {
                            // Save draft but don't navigate - let RouteLeavingGuard use lastLocation
                            // to navigate to the originally intended destination
                            onConfirm: () => {
                                onSaveDraftClick();
                            },
                            // Don't navigate - let RouteLeavingGuard use lastLocation
                            onCancel: () => {},
                        })}
                    />
                    <SplitRewireConfirmDialog
                        affected={splitRewireAffected}
                        confirmButtonText={
                            pendingPausedActionRef.current === 'saveAndResume' ? 'Save and Resume' : 'Save'
                        }
                        open={splitRewireDialogOpen}
                        onCancel={() => setSplitRewireDialogOpen(false)}
                        onConfirm={() => {
                            setSplitRewireDialogOpen(false);
                            if (pendingPausedActionRef.current === 'saveAndResume') {
                                doPublishAndResume();
                            } else {
                                doPublish();
                            }
                        }}
                    />
                    <SuccessDialog
                        title={isSaveDraftClicked ? 'Draft Saved' : 'high five!'}
                        body={isSaveDraftClicked ? DraftSuccessBody : PublishSuccessBody}
                        isOpen={isSuccessDialogOpen}
                        setIsOpen={setIsSuccessDialogOpen}
                        onClose={() => {
                            setIsSuccessDialogOpen(false);
                            setIsSaveDraftClicked(false); // Reset draft state when dialog is closed
                            setSavedDraftCampaignId(null); // Reset saved campaign ID
                        }}
                        buttonOneText={isSaveDraftClicked ? 'Continue' : 'Insights'}
                        buttonTwoText={isSaveDraftClicked ? 'Exit' : 'Curations'}
                        buttonOneOnClick={
                            isSaveDraftClicked
                                ? () => {
                                      setIsSuccessDialogOpen(false);
                                      setIsSaveDraftClicked(false); // Reset draft state
                                      // Navigate to the saved draft campaign for editing
                                      if (savedDraftCampaignId) {
                                          const targetCampaignId = savedDraftCampaignId;
                                          setSavedDraftCampaignId(null); // Reset saved campaign ID
                                          history.replace(`${URLPaths.CURATIONS_CUSTOM}/${targetCampaignId}`);
                                      }
                                  }
                                : () => handleExitCurationWorkflow(URLPaths.INSIGHTS)
                        }
                        buttonTwoOnClick={
                            isSaveDraftClicked
                                ? () => handleExitCurationWorkflow(URLPaths.CURATIONS)
                                : () => handleExitCurationWorkflow(URLPaths.CURATIONS)
                        }
                    />
                </>
            }
            topLeft={
                activeStep === CustomCampaignSteps.NAME_CAMPAIGN ? null : (
                    <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 2 }}>
                        <CustomCampaignNameInput
                            name={campaignState.name}
                            handleNameChange={(name: string) => dispatchCampaignAction(setName(name))}
                            withClickAway
                            disabled={disableNameEditing}
                        />
                        {status && (
                            <Box sx={{ pt: 3 }}>
                                <CampaignStatusText status={status} />
                            </Box>
                        )}
                    </Box>
                )
            }
            topRight={
                <CustomCampaignNavigationButtons
                    activeStep={activeStep}
                    continueTooltip={continueTooltip}
                    isContinueEnabled={isContinueEnabled}
                    isPublishEnabled={isPublishEnabled}
                    publishDisabledTooltip={publishDisabledTooltip}
                    onContinueClick={onContinueClick}
                    onPublishClick={onPublishClick}
                    onPublishAndResumeClick={onPublishAndResumeClick}
                    onSaveDraftClick={onSaveDraftClick}
                    setContinueTooltip={setContinueTooltip}
                    setIsContinueEnabled={setIsContinueEnabled}
                />
            }
            bottomLeft={
                <CustomCampaignForm
                    activeSendIndex={activeSendIndex}
                    activeStep={activeStep}
                    campaignState={campaignState}
                    contentPreviewId={contentPreviewId}
                    customContinueHandler={customContinueHandler}
                    dispatchCampaignAction={dispatchCampaignAction}
                    initialContentSubStep={initialContentSubStep}
                    navigateToABTestScheduling={navigateToABTestScheduling}
                    setActiveSendIndex={setActiveSendIndex}
                    setActiveStep={setActiveStep}
                    setContentPreviewId={setContentPreviewId}
                    setCustomContinueHandler={setCustomContinueHandlerFixed}
                    setContinueTooltip={setContinueTooltip}
                    setIsContinueEnabled={setIsContinueEnabled}
                    setNextStep={setNextStep}
                    onContentUnavailableChange={handleContentUnavailableChange}
                />
            }
            bottomRight={
                activeStep === CustomCampaignSteps.NAME_CAMPAIGN ? null : contentPreviewId ? (
                    <CustomCampaignContentPreview
                        activeSendIndex={activeSendIndex}
                        campaignState={campaignState}
                        contentPreviewId={contentPreviewId}
                        setContentPreviewId={setContentPreviewId}
                    />
                ) : (
                    <div style={navigationSx}>
                        {hasCampaignGraphNavigationFlag ? (
                            <GraphCampaignNavigation
                                activeSendIndex={activeSendIndex}
                                activeStep={activeStep}
                                campaignState={campaignState}
                                onRemoveClick={onRemoveClick}
                                setActiveSendIndex={setActiveSendIndex}
                                setActiveStep={setActiveStep}
                            />
                        ) : (
                            <CustomCampaignNavigation
                                activeSendIndex={activeSendIndex}
                                activeStep={activeStep}
                                campaignState={campaignState}
                                onRemoveClick={onRemoveClick}
                                setActiveSendIndex={setActiveSendIndex}
                                setActiveStep={setActiveStep}
                            />
                        )}
                    </div>
                )
            }
        />
    );
};

export default CustomCampaign;
